import {
  ApprovalGroupPicker,
  DateRange,
  FacilityPicker,
  LinkedEntityPicker,
  NumericRange,
  PipelineStepPicker,
  ProjectPicker,
  SearchQuery,
  UserPicker,
  ValuePicker,
  WorkdayDepartmentPicker,
  WorkdayCostCenterPicker,
} from 'components/tables/column-filters'
import type { ReportTypeMap, ReportFilterValue } from '../types'
import { ReactNode } from 'react'
import { TFunction } from 'react-i18next'
import {
  AuditLogTargetType,
  AuditLogEditField,
  StatusWithCorrected,
  AuditLogEventType,
  TimeLogReportEntryType,
  ProjectIntegrationType,
  WorkerTypeEnum,
  ContingentWorkerType,
} from 'types'
import { normalizeValue } from '../utils/normalizeValue'
import { secondsToHHMM } from '@utils/time'
import { DateTime } from 'luxon'
import { translationForAuditLogTargetType } from '../utils/translationForAuditLogTargetType'
import { translationForAuditLogEditField } from '../utils/translationForAuditLogEditField'
import { ProjectIntegrationPicker } from 'components/tables/column-filters/project-integration-picker'
import { toDotSnakeCase } from '@utils/toDotSnakeCase'

const handleValuesChange =
  <T extends string | number>(
    cb: (arg: { values?: T[] } | undefined) => void,
  ) =>
  (selected: Set<T>) => {
    const values = normalizeValue(Array.from(selected))
    cb(values && { values })
  }

const toSet = <T,>(v: T[] | undefined): Set<T> => {
  return new Set(v ?? [])
}

type TranslationKey = Parameters<TFunction<'translation', undefined>>[0]

export interface RenderFilterOptions<V> {
  value: V
  onChange: (v: V) => void
  weekStartsOn: 'monday' | 'sunday'
}

class Info<
  T,
  RT extends keyof ReportTypeMap,
  FK extends keyof ReportTypeMap[RT]['filters'],
> {
  public sortKey: ReportTypeMap[RT]['pageColumn']
  /**
   * snake_case nested key of the column within the report page row separated by hyphens
   * This is useful when referencing a column on the backend.  Its structure is inspired
   * by the nested shape of the report page row.
   */
  public snakeCaseAccessorKey: string
  /**
   * translation key for the column label
   */
  public labelKey: TranslationKey
  /**
   * camelCased nested key of the column within the report page row separated by hyphens
   */
  public accessorKey: ReportTypeMap[RT]['pageColumn']
  public accessor: (
    row: ReportTypeMap[RT]['pageRow'],
    injectedHelpers: {
      t: TFunction<'translation', undefined>
      toLocaleString: (
        args: Intl.DateTimeFormatOptions,
      ) => (date: DateTime) => string
    },
  ) => T
  public filterKey: FK
  public renderFilter: (
    options: RenderFilterOptions<ReportFilterValue<RT, FK>>,
  ) => ReactNode

  constructor(params: {
    /**
     * translation key for the column label, helpful for rendering out column headers
     */
    labelKey: TranslationKey
    /**
     * camelCased nested key of the column within the report page row separated by dots
     */
    accessorKey: ReportTypeMap[RT]['pageColumn']
    accessor: (
      row: ReportTypeMap[RT]['pageRow'],
      injectedHelpers: {
        t: TFunction<'translation', undefined>
        toLocaleString: (
          args: Intl.DateTimeFormatOptions,
        ) => (date: DateTime) => string
      },
    ) => T
    filterKey: FK
    renderFilter: (
      options: RenderFilterOptions<ReportFilterValue<RT, FK>>,
    ) => ReactNode
    /**
     * maps to how the api sorts results
     */
    sortKey?: ReportTypeMap[RT]['pageColumn']
  }) {
    const {
      labelKey,
      accessorKey,
      accessor,
      filterKey,
      renderFilter,
      sortKey,
    } = params
    this.labelKey = labelKey
    this.accessorKey = accessorKey
    this.accessor = accessor
    this.filterKey = filterKey
    this.renderFilter = renderFilter
    this.sortKey = sortKey ?? this.accessorKey
    this.snakeCaseAccessorKey = toDotSnakeCase(this.accessorKey)
  }
}

type InfoMap<RT extends keyof ReportTypeMap> = Record<
  ReportTypeMap[RT]['columnId'],
  Info<
    string | number | null | undefined,
    RT,
    keyof ReportTypeMap[RT]['filters']
  >
>

const infoByTimeLogColumnId: InfoMap<'time_log'> = {
  'approval-group.id': new Info({
    labelKey: 'features.reporting.id',
    accessorKey: 'approvalGroup.id',
    accessor: (row) => row.approvalGroup.id,
    filterKey: 'approvalGroup.id',
    renderFilter: ({ value, onChange }) => (
      <ApprovalGroupPicker
        columnNameTranslationKey="features.reporting.timeLog.approvalGroupId"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
  }),
  'approval-group.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'approvalGroup.name',
    accessor: (row) => row.approvalGroup.name,
    filterKey: 'approvalGroup.id',
    renderFilter: ({ value, onChange }) => (
      <ApprovalGroupPicker
        columnNameTranslationKey="features.reporting.timeLog.approvalGroupName"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
    sortKey: 'approvalGroup.name',
  }),
  'linked-entity.type': new Info({
    labelKey: 'features.reporting.type',
    accessorKey: 'linkedEntity.type',
    accessor: (row) => row.linkedEntity.type,
    filterKey: 'linkedEntity.type',
    renderFilter: ({ value, onChange }) => (
      <LinkedEntityPicker
        columnNameTranslationKey="features.reporting.timeLog.linkedEntityType"
        onChange={handleValuesChange(onChange)}
        selected={toSet(value?.values)}
      />
    ),
  }),
  'linked-entity.name': new Info({
    labelKey: 'features.reporting.timeLog.nameOrCode',
    accessorKey: 'linkedEntity.name',
    accessor: (row) => row.linkedEntity.name,
    filterKey: 'linkedEntity.name',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.linkedEntityNameOrCode"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'linked-entity.shotgrid-id': new Info({
    labelKey: 'features.reporting.id',
    accessorKey: 'linkedEntity.shotgridId',
    accessor: (row) => row.linkedEntity.shotgridId,
    filterKey: 'linkedEntity.shotgridId',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.linkedEntityShotgridId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'project-integration.id': new Info({
    labelKey: 'features.reporting.id',
    accessorKey: 'projectIntegration.id',
    accessor: (row) => row.projectIntegration.id,
    filterKey: 'projectIntegration.id',
    renderFilter: ({ value, onChange }) => (
      <ProjectIntegrationPicker
        columnNameTranslationKey="common.projectIntegration"
        selectedIds={toSet(value?.values)}
        onChange={handleValuesChange(onChange)}
      />
    ),
  }),
  'project-integration.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'projectIntegration.name',
    accessor: (row) => row.projectIntegration.name,
    filterKey: 'projectIntegration.id',
    renderFilter: ({ value, onChange }) => (
      <ProjectIntegrationPicker
        columnNameTranslationKey="common.projectIntegration"
        selectedIds={toSet(value?.values)}
        onChange={handleValuesChange(onChange)}
      />
    ),
    sortKey: 'project.name',
  }),
  'project-integration.type': new Info({
    labelKey: 'features.reporting.type',
    accessorKey: 'projectIntegration.type',
    accessor: (row) => row.projectIntegration.type,
    filterKey: 'projectIntegration.type',
    renderFilter: ({ value, onChange }) => (
      <ValuePicker
        accessor={(v) => v}
        columnNameTranslationKey="features.reporting.type"
        onChange={(selected) => {
          const types = normalizeValue(Array.from(selected))
          onChange(types && { values: types })
        }}
        renderLabel={(type, t) => t(`projectIntegrationTypes.${type}`)}
        selected={new Set(value?.values)}
        values={Object.values(ProjectIntegrationType)}
      />
    ),
  }),
  'project.id': new Info({
    labelKey: 'features.reporting.timeLog.timeLoggerId',
    accessorKey: 'project.id',
    accessor: (row) => row.project.id,
    filterKey: 'project.id',
    renderFilter: ({ value, onChange }) => (
      <ProjectPicker
        columnNameTranslationKey="features.reporting.timeLog.timeLoggerProject"
        selectedIds={toSet(value?.values)}
        onChange={handleValuesChange(onChange)}
      />
    ),
  }),
  'project.source-id': new Info({
    labelKey: 'features.reporting.timeLog.sourceId',
    accessorKey: 'project.sourceId',
    accessor: (row) => row.project.sourceId,
    filterKey: 'project.sourceId',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.sourceId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'project.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'project.name',
    accessor: (row) => row.project.name,
    filterKey: 'project.id',
    renderFilter: ({ value, onChange }) => (
      <ProjectPicker
        columnNameTranslationKey="features.reporting.timeLog.timeLoggerProject"
        selectedIds={toSet(value?.values)}
        onChange={handleValuesChange(onChange)}
      />
    ),
    sortKey: 'project.name',
  }),
  'project.workday-id': new Info({
    labelKey: 'features.reporting.timeLog.workdayId',
    accessorKey: 'project.workdayId',
    accessor: (row) => row.project.workdayId,
    filterKey: 'project.workdayId',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.projectWorkdayID"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'time-log.date': new Info({
    labelKey: 'features.reporting.date',
    accessorKey: 'date',
    accessor: (row, { toLocaleString }) =>
      row.date && toLocaleString(DateTime.DATE_SHORT)(row.date),
    filterKey: 'date',
    renderFilter: ({ value, onChange, weekStartsOn }) => (
      <DateRange
        endDate={value?.end}
        startDate={value?.start}
        relativeDateRangeValue={value?.relativeDateRange}
        onChange={({ startDate, endDate, relativeDateRangeValue }) =>
          onChange(
            normalizeValue({
              start: startDate,
              end: endDate,
              relativeDateRange: relativeDateRangeValue,
            }),
          )
        }
        weekStartsOn={weekStartsOn}
      />
    ),
  }),
  'linked-entity.sequence-code': new Info({
    labelKey: 'features.reporting.timeLog.sequenceCode',
    accessorKey: 'linkedEntity.sequence.code',
    accessor: (row) => row.linkedEntity.sequence?.code,
    filterKey: 'linkedEntity.sequence.code',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.linkedEntitySequenceCode"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'pipeline-step.code': new Info({
    labelKey: 'common.code',
    accessorKey: 'pipelineStep.code',
    accessor: (row) => row.pipelineStep.code,
    filterKey: 'pipelineStep.id',
    renderFilter: ({ value, onChange }) => (
      <PipelineStepPicker
        columnNameTranslationKey="common.code"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
    sortKey: 'pipelineStep.code',
  }),
  'facility.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'facility.name',
    accessor: (row) => row.facility.name,
    filterKey: 'facility.id',
    renderFilter: ({ value, onChange }) => (
      <FacilityPicker
        columnNameTranslationKey="features.reporting.facilityName"
        selectedIds={toSet(value?.values)}
        onChange={handleValuesChange(onChange)}
      />
    ),
    sortKey: 'facility.name',
  }),
  'user.workday-worker-id': new Info({
    labelKey: 'features.reporting.timeLog.wid',
    accessorKey: 'employee.workdayWorkerId',
    accessor: (row) => row.employee.workdayWorkerId,
    filterKey: 'employee.workdayWorkerId',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.workdayWorkerId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'time-card.sent-to-workday-datetime': new Info({
    labelKey: 'features.reporting.sentToWorkday',
    accessorKey: 'sentToWorkdayTimestamp',
    accessor: (row, { toLocaleString }) =>
      row.sentToWorkdayTimestamp &&
      toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)(
        row.sentToWorkdayTimestamp,
      ),
    filterKey: 'sentToWorkdayTimestamp',
    renderFilter: ({ value, onChange, weekStartsOn }) => (
      <DateRange
        endDate={value?.end}
        startDate={value?.start}
        relativeDateRangeValue={value?.relativeDateRange}
        onChange={({ startDate, endDate, relativeDateRangeValue }) =>
          onChange(
            normalizeValue({
              start: startDate,
              end: endDate,
              relativeDateRange: relativeDateRangeValue,
            }),
          )
        }
        weekStartsOn={weekStartsOn}
      />
    ),
  }),
  'time-log.duration': new Info({
    labelKey: 'features.reporting.timeLog.duration',
    accessorKey: 'durationInSeconds',
    accessor: (row) => secondsToHHMM(row.durationInSeconds),
    filterKey: 'durationInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.duration"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.work-duration': new Info({
    labelKey: 'features.reporting.timeLog.workDuration',
    accessorKey: 'workDurationInSeconds',
    accessor: (row) =>
      row.workDurationInSeconds && secondsToHHMM(row.workDurationInSeconds),
    filterKey: 'workDurationInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.workDuration"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.straight-time': new Info({
    labelKey: 'features.reporting.timeLog.straightTimeAbbr',
    accessorKey: 'straightTimeInSeconds',
    accessor: (row) =>
      row.straightTimeInSeconds && secondsToHHMM(row.straightTimeInSeconds),
    filterKey: 'straightTimeInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.straightTime"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.overtime': new Info({
    labelKey: 'features.reporting.timeLog.overtimeAbbr',
    accessorKey: 'overtimeInSeconds',
    accessor: (row) =>
      row.overtimeInSeconds && secondsToHHMM(row.overtimeInSeconds),
    filterKey: 'overtimeInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.overtime"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.double-time': new Info({
    labelKey: 'features.reporting.timeLog.doubleTimeAbbr',
    accessorKey: 'doubleTimeInSeconds',
    accessor: (row) =>
      row.doubleTimeInSeconds && secondsToHHMM(row.doubleTimeInSeconds),
    filterKey: 'doubleTimeInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.doubleTime"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.toil': new Info({
    labelKey: 'features.reporting.timeLog.toil',
    accessorKey: 'toilInSeconds',
    accessor: (row) => row.toilInSeconds && secondsToHHMM(row.toilInSeconds),
    filterKey: 'toilInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.toil"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.toil1_5x': new Info({
    labelKey: 'features.reporting.timeLog.toil1_5x',
    accessorKey: 'toil_1_5xInSeconds',
    accessor: (row) =>
      row.toil_1_5xInSeconds && secondsToHHMM(row.toil_1_5xInSeconds),
    filterKey: 'toil_1_5xInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.toil1_5x"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.time-off-duration': new Info({
    labelKey: 'features.reporting.timeLog.timeOffDuration',
    accessorKey: 'timeOffInSeconds',
    accessor: (row) =>
      row.timeOffInSeconds && secondsToHHMM(row.timeOffInSeconds),
    filterKey: 'timeOffInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.timeOffDuration"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.leave-duration': new Info({
    labelKey: 'features.reporting.timeLog.leaveDuration',
    accessorKey: 'leaveInSeconds',
    accessor: (row) => row.leaveInSeconds && secondsToHHMM(row.leaveInSeconds),
    filterKey: 'leaveInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.leaveDuration"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.holiday-duration': new Info({
    labelKey: 'features.reporting.timeLog.holiday',
    accessorKey: 'holidayInSeconds',
    accessor: (row) =>
      row.holidayInSeconds && secondsToHHMM(row.holidayInSeconds),
    filterKey: 'holidayInSeconds',
    renderFilter: ({ value, onChange }) => (
      <NumericRange
        columnNameTranslationKey="features.reporting.timeLog.holiday"
        from={value?.numericRangeFrom}
        to={value?.numericRangeTo}
        onChange={(value) =>
          onChange(
            normalizeValue({
              numericRangeFrom: value.from,
              numericRangeTo: value.to,
            }),
          )
        }
      />
    ),
  }),
  'time-log.task-source-id': new Info({
    labelKey: 'features.reporting.timeLog.taskSourceId',
    accessorKey: 'taskSourceTimeLogId',
    accessor: (row) => row.taskSourceTimeLogId,
    filterKey: 'taskSourceTimeLogId',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.taskSourceTimeLogId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'user.task-source-id': new Info({
    labelKey: 'features.reporting.timeLog.taskSourceId',
    accessorKey: 'user.taskSourceUserId',
    accessor: (row) => row.user.taskSourceUserId,
    filterKey: 'user.taskSourceUserId',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.taskSourceId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'task.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'task.name',
    accessor: (row) => row.task.name,
    filterKey: 'task.name',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.taskName"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'task.id': new Info({
    labelKey: 'features.reporting.timeLog.timeLoggerId',
    accessorKey: 'task.id',
    accessor: (row) => row.task.id,
    filterKey: 'task.id',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.timeLoggerTaskId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'task.source-id': new Info({
    labelKey: 'features.reporting.timeLog.sourceId',
    accessorKey: 'task.sourceId',
    accessor: (row) => row.task.sourceId,
    filterKey: 'task.sourceId',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.sourceId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'task.sg-shot-code': new Info({
    labelKey: 'features.reporting.timeLog.sgShot',
    accessorKey: 'task.sgShot.code',
    accessor: (row) => row.task.sgShot.code,
    filterKey: 'task.sgShot.code',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.sgShot"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'task.jira-issue-path': new Info({
    labelKey: 'features.reporting.timeLog.jiraPath',
    accessorKey: 'task.jiraIssuePath',
    accessor: (row) => row.task.jiraIssuePath,
    filterKey: 'task.jiraIssuePath',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.jiraIssuePath"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'time-log.status': new Info({
    labelKey: 'features.reporting.timeLog.status',
    accessorKey: 'status',
    accessor: (row, { t }) => {
      if (row.status === null) return
      if (!t) return row.status

      return t(`common.statuses.${row.status}`)
    },
    filterKey: 'status',
    renderFilter: ({ value, onChange }) => (
      <ValuePicker
        accessor={(v) => v}
        columnNameTranslationKey="features.reporting.timeLog.timeLogStatus"
        onChange={(selected) => {
          const statuses = normalizeValue(Array.from(selected))
          onChange(statuses && { statuses })
        }}
        renderLabel={(status, t) => t(`common.statuses.${status}`)}
        selected={new Set(value?.statuses)}
        values={[
          StatusWithCorrected.Open,
          StatusWithCorrected.Submitted,
          StatusWithCorrected.Approved,
          StatusWithCorrected.Sent,
          StatusWithCorrected.Corrected,
        ]}
      />
    ),
  }),
  'time-log.created-at': new Info({
    labelKey: 'features.reporting.timeLog.createdAt',
    accessorKey: 'createdAt',
    accessor: ({ createdAt }, { toLocaleString }) =>
      createdAt &&
      toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)(createdAt),
    filterKey: 'createdAt',
    renderFilter: ({ value, onChange, weekStartsOn }) => (
      <DateRange
        endDate={value?.end}
        startDate={value?.start}
        relativeDateRangeValue={value?.relativeDateRange}
        onChange={({ startDate, endDate, relativeDateRangeValue }) =>
          onChange(
            normalizeValue({
              start: startDate,
              end: endDate,
              relativeDateRange: relativeDateRangeValue,
            }),
          )
        }
        weekStartsOn={weekStartsOn}
      />
    ),
  }),
  'time-log.updated-at': new Info({
    labelKey: 'features.reporting.timeLog.updatedAt',
    accessorKey: 'updatedAt',
    accessor: ({ updatedAt }, { toLocaleString }) =>
      updatedAt &&
      toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)(updatedAt),
    filterKey: 'updatedAt',
    renderFilter: ({ value, onChange, weekStartsOn }) => (
      <DateRange
        endDate={value?.end}
        startDate={value?.start}
        relativeDateRangeValue={value?.relativeDateRange}
        onChange={({ startDate, endDate, relativeDateRangeValue }) =>
          onChange(
            normalizeValue({
              start: startDate,
              end: endDate,
              relativeDateRange: relativeDateRangeValue,
            }),
          )
        }
        weekStartsOn={weekStartsOn}
      />
    ),
  }),
  'user.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'user.name',
    accessor: (row) => row.user.name,
    filterKey: 'user.id',
    renderFilter: ({ value, onChange }) => (
      <UserPicker
        columnNameTranslationKey="features.reporting.userName"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
    sortKey: 'user.name',
  }),
  'user.email': new Info({
    labelKey: 'common.email',
    accessorKey: 'user.email',
    accessor: (row) => row.user.email,
    filterKey: 'user.email',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.userEmail"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'department.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'workday.department.name',
    accessor: (row) => row.workday.department.name,
    filterKey: 'workday.department.id',
    renderFilter: ({ value, onChange }) => (
      <WorkdayDepartmentPicker
        columnNameTranslationKey="features.reporting.timeLog.departmentName"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
    sortKey: 'workday.department.name',
  }),
  'cost-center.code': new Info({
    labelKey: 'common.code',
    accessorKey: 'workday.costCenter.code',
    accessor: (row) => row.workday.costCenter.code,
    filterKey: 'workday.costCenter.id',
    renderFilter: ({ value, onChange }) => (
      <WorkdayCostCenterPicker
        columnNameTranslationKey="common.costCenter"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
    sortKey: 'workday.costCenter.code',
  }),
  'cost-center.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'workday.costCenter.name',
    accessor: (row) => row.workday.costCenter.name,
    filterKey: 'workday.costCenter.id',
    renderFilter: ({ value, onChange }) => (
      <WorkdayCostCenterPicker
        columnNameTranslationKey="common.costCenter"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
    sortKey: 'workday.costCenter.name',
  }),
  'time-log.type': new Info({
    labelKey: 'features.reporting.type',
    accessorKey: 'type',
    accessor: (row, { t }) => {
      if (row.type === null) return
      if (!t) return row.type

      return t(`features.reporting.timeLog.types.${row.type}`)
    },
    filterKey: 'type',
    renderFilter: ({ value, onChange }) => (
      <ValuePicker
        accessor={(v) => v}
        columnNameTranslationKey="features.reporting.type"
        onChange={(selected) => {
          const types = normalizeValue(Array.from(selected))
          onChange(types && { values: types })
        }}
        renderLabel={(type, t) => t(`features.reporting.timeLog.types.${type}`)}
        selected={new Set(value?.values)}
        values={Object.values(TimeLogReportEntryType)}
      />
    ),
  }),
  'absence.workday-type': new Info({
    labelKey: 'features.reporting.timeLog.workdayType',
    accessorKey: 'workday.absenceType',
    accessor: (row) => row.workday.absenceType,
    filterKey: 'workday.absenceType',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.workdayType"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'holiday.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'workday.holiday.name',
    accessor: (row) => row.workday.holiday.name,
    filterKey: 'workday.holiday.name',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.filterColumns.workday.holiday.name"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'note.body': new Info({
    labelKey: 'features.reporting.timeLog.bodyNote',
    accessorKey: 'note.body',
    accessor: (row) => row.note.body,
    filterKey: 'note.body',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.filterColumns.note.body"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'worker.type': new Info({
    labelKey: 'features.reporting.timeLog.type',
    accessorKey: 'workday.workerType',
    accessor: (row, { t }) => {
      if (!t) return row.workday.workerType

      return t(`workerTypes.${row.workday.workerType}`)
    },
    filterKey: 'workday.workerType',
    renderFilter: ({ value, onChange }) => (
      <ValuePicker
        accessor={(v) => v}
        columnNameTranslationKey="features.reporting.timeLog.type"
        onChange={(selected) => {
          const workerTypes = normalizeValue(Array.from(selected))
          onChange(workerTypes && { values: workerTypes })
        }}
        renderLabel={(workerType, t) => t(`workerTypes.${workerType}`)}
        selected={new Set(value?.values)}
        values={[WorkerTypeEnum.Employee, WorkerTypeEnum.ContingentWorker]}
      />
    ),
  }),
  'worker.contingent-worker-type': new Info({
    labelKey: 'features.reporting.timeLog.cwType',
    accessorKey: 'workday.contingentWorkerType',
    accessor: (row, { t }) => {
      if (row.workday.contingentWorkerType === null) return

      return t(`contingentWorkerTypes.${row.workday.contingentWorkerType}`)
    },
    filterKey: 'workday.contingentWorkerType',
    renderFilter: ({ value, onChange }) => (
      <ValuePicker
        accessor={(v) => v}
        columnNameTranslationKey="features.reporting.timeLog.contingentWorkerType"
        onChange={(selected) => {
          const contingentWorkerTypes = normalizeValue(Array.from(selected))
          onChange(contingentWorkerTypes && { values: contingentWorkerTypes })
        }}
        renderLabel={(contingentWorkerType, t) =>
          t(`contingentWorkerTypes.${contingentWorkerType}`)
        }
        selected={new Set(value?.values)}
        values={[
          ContingentWorkerType.IndependentContractor,
          ContingentWorkerType.Temporary,
          ContingentWorkerType.Vendor,
        ]}
      />
    ),
  }),
}

const infoByAuditLogColumnId: InfoMap<'audit_log'> = {
  'event.type': new Info({
    labelKey: 'features.reporting.type',
    accessorKey: 'eventType',
    accessor: (row, { t }) =>
      t(`features.reporting.auditLog.auditLogEventTypes.${row.eventType}`),
    filterKey: 'eventType',
    renderFilter: ({ value, onChange }) => (
      <ValuePicker
        accessor={(v) => v}
        columnNameTranslationKey="features.reporting.auditLog.eventType"
        onChange={(selected) => {
          const types = normalizeValue(Array.from(selected))
          onChange(types && { values: types })
        }}
        renderLabel={(type, t) =>
          t(`features.reporting.auditLog.auditLogEventTypes.${type}`)
        }
        selected={new Set(value?.values)}
        values={Object.values(AuditLogEventType)}
        searchable={true}
      />
    ),
  }),
  'event.datetime': new Info({
    labelKey: 'features.reporting.auditLog.datetime',
    accessorKey: 'datetime',
    accessor: (row, { toLocaleString }) =>
      toLocaleString(DateTime.DATETIME_SHORT)(row.datetime),
    filterKey: 'datetime',
    renderFilter: ({ value, onChange, weekStartsOn }) => (
      <DateRange
        endDate={value?.end}
        startDate={value?.start}
        relativeDateRangeValue={value?.relativeDateRange}
        onChange={({ startDate, endDate, relativeDateRangeValue }) =>
          onChange(
            normalizeValue({
              start: startDate,
              end: endDate,
              relativeDateRange: relativeDateRangeValue,
            }),
          )
        }
        weekStartsOn={weekStartsOn}
      />
    ),
  }),
  'actor.id': new Info({
    labelKey: 'features.reporting.id',
    accessorKey: 'actorUser.id',
    accessor: (row) => row.actorUser.id,
    filterKey: 'actorUser.id',
    renderFilter: ({ value, onChange }) => (
      <UserPicker
        columnNameTranslationKey="features.reporting.userName"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
  }),
  'actor.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'actorUser.name',
    accessor: (row) => row.actorUser.name,
    filterKey: 'actorUser.id',
    renderFilter: ({ value, onChange }) => (
      <UserPicker
        columnNameTranslationKey="features.reporting.userName"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
  }),
  'actor.email': new Info({
    labelKey: 'common.email',
    accessorKey: 'actorUser.email',
    accessor: (row) => row.actorUser.email,
    filterKey: 'actorUser.email',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="common.email"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'target-entity.type': new Info({
    labelKey: 'features.reporting.type',
    accessorKey: 'target.type',
    accessor: (row, { t }) =>
      t(translationForAuditLogTargetType(row.target.type)),
    filterKey: 'target.type',
    renderFilter: ({ value, onChange }) => (
      <ValuePicker
        accessor={(v) => v}
        columnNameTranslationKey="features.reporting.entityType"
        onChange={(selected) => {
          const types = normalizeValue(Array.from(selected))
          onChange(types && { values: types })
        }}
        renderLabel={(type, t) => t(translationForAuditLogTargetType(type))}
        selected={new Set(value?.values)}
        values={Object.values(AuditLogTargetType)}
      />
    ),
  }),
  'target-entity.id': new Info({
    labelKey: 'features.reporting.id',
    accessorKey: 'target.id',
    accessor: (row) => row.target.id,
    filterKey: 'target.id',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.timeLog.entityId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'target-entity.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'target.name',
    accessor: (row) => row.target.name,
    filterKey: 'target.name',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.entityName"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'target-user.id': new Info({
    labelKey: 'features.reporting.id',
    accessorKey: 'targetUser.id',
    accessor: (row) => row.targetUser.id,
    filterKey: 'targetUser.id',
    renderFilter: ({ value, onChange }) => (
      <UserPicker
        columnNameTranslationKey="features.reporting.userName"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
  }),
  'target-user.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'targetUser.name',
    accessor: (row) => row.targetUser.name,
    filterKey: 'targetUser.id',
    renderFilter: ({ value, onChange }) => (
      <UserPicker
        columnNameTranslationKey="features.reporting.userName"
        onChange={handleValuesChange(onChange)}
        selectedIds={toSet(value?.values)}
      />
    ),
  }),
  'target-user.email': new Info({
    labelKey: 'common.email',
    accessorKey: 'targetUser.email',
    accessor: (row) => row.targetUser.email,
    filterKey: 'targetUser.email',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.entityName"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'target.date': new Info({
    labelKey: 'features.reporting.date',
    accessorKey: 'targetDate',
    accessor: (row, { toLocaleString }) =>
      row.targetDate && toLocaleString(DateTime.DATE_SHORT)(row.targetDate),
    filterKey: 'targetDate',
    renderFilter: ({ value, onChange, weekStartsOn }) => (
      <DateRange
        endDate={value?.end}
        startDate={value?.start}
        relativeDateRangeValue={value?.relativeDateRange}
        onChange={({ startDate, endDate, relativeDateRangeValue }) =>
          onChange(
            normalizeValue({
              start: startDate,
              end: endDate,
              relativeDateRange: relativeDateRangeValue,
            }),
          )
        }
        weekStartsOn={weekStartsOn}
      />
    ),
  }),
  'field-edited.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'fieldEdited.name',
    accessor: (row, { t }) =>
      row.fieldEdited.name &&
      t(translationForAuditLogEditField(row.fieldEdited.name)),
    filterKey: 'fieldEdited.name',
    renderFilter: ({ value, onChange }) => (
      <ValuePicker
        accessor={(v) => v}
        columnNameTranslationKey="features.reporting.auditLog.fieldName"
        onChange={(selected) => {
          const fields = normalizeValue(Array.from(selected))
          onChange(fields && { values: fields })
        }}
        renderLabel={(type, t) => t(translationForAuditLogEditField(type))}
        selected={new Set(value?.values)}
        values={Object.values(AuditLogEditField)}
      />
    ),
  }),
  'field-edited.old-value': new Info({
    labelKey: 'features.reporting.auditLog.oldValue',
    accessorKey: 'fieldEditedFromValue.text',
    accessor: (row) => row.fieldEditedFromValue.text,
    filterKey: 'fieldEditedFromValue.text',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.auditLog.oldValue"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'field-edited.old-id': new Info({
    labelKey: 'features.reporting.auditLog.oldId',
    accessorKey: 'fieldEditedFromValue.id',
    accessor: (row) => row.fieldEditedFromValue.id,
    filterKey: 'fieldEditedFromValue.id',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.auditLog.oldId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'field-edited.new-value': new Info({
    labelKey: 'features.reporting.auditLog.newValue',
    accessorKey: 'fieldEditedToValue.text',
    accessor: (row) => row.fieldEditedToValue.text,
    filterKey: 'fieldEditedToValue.text',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.auditLog.newValue"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'field-edited.new-id': new Info({
    labelKey: 'features.reporting.auditLog.newId',
    accessorKey: 'fieldEditedToValue.id',
    accessor: (row) => row.fieldEditedToValue.id,
    filterKey: 'fieldEditedToValue.id',
    renderFilter: ({ value, onChange }) => (
      <SearchQuery
        columnNameTranslationKey="features.reporting.auditLog.newId"
        query={value?.query}
        onChange={(query) => onChange(normalizeValue({ query }))}
      />
    ),
  }),
  'facility.id': new Info({
    labelKey: 'features.reporting.id',
    accessorKey: 'facility.id',
    accessor: (row) => row.facility.id,
    filterKey: 'facility.id',
    renderFilter: ({ value, onChange }) => (
      <FacilityPicker
        columnNameTranslationKey="features.reporting.facilityName"
        selectedIds={toSet(value?.values)}
        onChange={handleValuesChange(onChange)}
      />
    ),
  }),
  'facility.name': new Info({
    labelKey: 'common.name',
    accessorKey: 'facility.name',
    accessor: (row) => row.facility.name,
    filterKey: 'facility.id',
    renderFilter: ({ value, onChange }) => (
      <FacilityPicker
        columnNameTranslationKey="features.reporting.facilityName"
        selectedIds={toSet(value?.values)}
        onChange={handleValuesChange(onChange)}
      />
    ),
  }),
}

export const columnInfoFor = <RT extends keyof ReportTypeMap>(
  reportType: RT,
) => {
  return {
    /* eslint-disable camelcase */
    time_log: infoByTimeLogColumnId,
    audit_log: infoByAuditLogColumnId,
    /* eslint-enable camelcase */
  }[reportType]
}

export const infoByColumnId = <RT extends keyof ReportTypeMap>({
  reportType,
  id,
}: {
  reportType: RT
  id: ReportTypeMap[RT]['columnId']
}) => (columnInfoFor(reportType) as InfoMap<RT>)[id]
